import React, { useState } from 'react';
import { Button, Menu, MenuItem, CircularProgress } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import api from '../config/api';
import { partnerOutlineBtnSx } from './PartnerListChrome';

const FORMATS = {
  xlsx: {
    labelKey: 'partnerExport.excel',
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  csv: {
    labelKey: 'partnerExport.csv',
    mime: 'text/csv;charset=utf-8;',
  },
};

/**
 * Downloads one partner dataset (trips, clients, bookings...) as Excel or CSV.
 * The export endpoints need the partner token, so the file is fetched as a blob
 * through the api client rather than linked to directly.
 */
const PartnerDataExportButton = ({ dataset = 'trips', label, sx, testId = 'partner-export-button' }) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const errorMessage = async (err) => {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (parsed?.error) return parsed.error;
      } catch (parseError) {
        // fall through to the generic message
      }
    }
    return data?.error || t('partnerExport.failed');
  };

  const download = async (format) => {
    setMenuAnchor(null);
    if (downloading) return;

    try {
      setDownloading(true);
      const response = await api.get(`/partners/export/${dataset}.${format}`, {
        responseType: 'blob',
        usePartnerAuth: true,
      });

      const blob = new Blob([response.data], { type: FORMATS[format].mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dejavunomad-${dataset}-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar(t('partnerExport.done'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(await errorMessage(err), { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button
        sx={{
          ...partnerOutlineBtnSx,
          '& .MuiButton-startIcon': {
            mr: 0.75,
            ml: 0,
            '& > *:nth-of-type(1)': { fontSize: 16 },
          },
          ...sx,
        }}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        disabled={downloading}
        data-testid={testId}
        startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
      >
        {downloading ? t('partnerExport.preparing') : (label || t('partnerExport.export'))}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {Object.entries(FORMATS).map(([format, config]) => (
          <MenuItem
            key={format}
            onClick={() => download(format)}
            data-testid={`${testId}-${format}`}
          >
            {t(config.labelKey)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PartnerDataExportButton;
